import ROUTES from '@/routes/routes'

export interface SetupWizardStepLink {
  label: string
  to: string
  state?: {
    openInvite?: boolean
    openTeams?: boolean
  }
}

export interface SetupWizardStep {
  id: string
  stepNumber: number
  title: string
  summary: string
  bullets: string[]
  detail: string
  links: SetupWizardStepLink[]
}

export const getSetupWizardSteps = (roomId: string): SetupWizardStep[] => [
  {
    id: 'events',
    stepNumber: 1,
    title: 'Create events and connect them to rounds',
    summary:
      'Start by defining the events that can shape the game, then attach each event to the appropriate round so the timeline runs as expected.',
    bullets: [
      'Open Scenario & Events and create one or more events for the room.',
      'Review the event details so each event clearly matches the story and intended learning outcome.',
      'Open Rounds & Timers and tie the event to the round where it should appear.',
      'You can also release events manually to any round when you need more control over the timing.',
    ],
    detail:
      'This sequence keeps the game flow consistent. Events should exist before you schedule them so rounds only reference content that is already ready to use, while still allowing manual event release when you want to intervene during play.',
    links: [
      {
        label: 'Open Scenario & Events',
        to: ROUTES.PROFESSOR.ROOM_CONFIGURE_SCENARIO(roomId),
      },
      {
        label: 'Open Rounds & Timers',
        to: ROUTES.PROFESSOR.ROOM_CONFIGURE_TIMERS(roomId),
      },
    ],
  },
  {
    id: 'advisor',
    stepNumber: 2,
    title: 'Configure advisor scenario and behavior',
    summary:
      'Set up each advisor with the scenario context and instructions they need before players begin using them.',
    bullets: [
      'Open Advisors and select the advisor you want to configure.',
      'Set the advisor prompt, scenario, greetings, and restrictions so the behavior is aligned with the game design.',
      'Upload advisor documents so the advisor can use the right supporting material.',
    ],
    detail:
      'A strong advisor setup gives players consistent guidance. Keep prompts focused, use clear restrictions, and upload only the documents that should inform that advisor.',
    links: [
      {
        label: 'Open Advisors',
        to: ROUTES.PROFESSOR.ROOM_CONFIGURE_ADVISORS(roomId),
      },
    ],
  },
  {
    id: 'world-setup',
    stepNumber: 3,
    title: 'Set up shared world behavior and resources',
    summary:
      'Define room-wide behavioral settings and shared resources that multiple advisors can reuse across the game.',
    bullets: [
      'Open World Setup and configure the shared behavioral settings for the room.',
      'Upload shared documents and resources that should be available at the room level.',
      'Enable the related world setup options in each advisor when you want that advisor to use room shared documents.',
    ],
    detail:
      'World Setup is useful when several advisors should work from the same shared knowledge base. It reduces repeated uploads and keeps cross-advisor context more consistent.',
    links: [
      {
        label: 'Open World Setup',
        to: ROUTES.PROFESSOR.ROOM_CONFIGURE_SHARED_ADVISOR_SETTINGS(roomId),
      },
      {
        label: 'Review Advisor Access',
        to: ROUTES.PROFESSOR.ROOM_CONFIGURE_ADVISORS(roomId),
      },
    ],
  },
  {
    id: 'invite',
    stepNumber: 4,
    title: 'Invite participants to the room',
    summary:
      'After the core setup is ready, open the invite flow and share access with professors and students so they join the configured room.',
    bullets: [
      'Open the Invite flow directly or Go to the control panel and click on the Invite button.',
      'Share access with the participants who should join the room.',
      'Confirm the room is configured before players enter so they see the intended setup from the start.',
    ],
    detail:
      'Inviting users after the main setup helps reduce confusion and avoids bringing players into an unfinished experience.',
    links: [
      {
        label: 'Open Invite',
        to: ROUTES.PROFESSOR.ROOM_DETAILS(roomId),
        state: {
          openInvite: true,
        },
      },
    ],
  },
  {
    id: 'teams',
    stepNumber: 5,
    title: 'Create teams and assign players and advisors',
    summary:
      'Finish by organizing teams, assigning players to those teams, and then mapping the right advisor to each team so the room is ready for play.',
    bullets: [
      'Open the Teams section in Advisor Settings and create the team first.',
      'Assign player(s) to the team.',
      'Assign advisor(s) to the team.',
      'Review the mapping so every team has the right players and advisor support before the game starts.',
    ],
    detail:
      'Creating teams before assignment avoids broken mappings and makes it easier to verify that each team is fully prepared with both its players and advisor.',
    links: [
      {
        label: 'Open Teams',
        to: ROUTES.PROFESSOR.ROOM_CONFIGURE_ADVISORS(roomId),
        state: {
          openTeams: true,
        },
      },
    ],
  },
]
